import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { ArrowLeft, ArrowRight, Sparkles, CheckCircle2 } from "lucide-react";
import { QUESTIONS } from "../../lib/questionnaire";
import { toast } from "sonner";
import { t } from "../../lib/i18n";

export default function Questionnaire() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [answers, setAnswers] = useState({}); // { "1": {...}, "2": {...}, ... }
  const [idx, setIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [aiMessage, setAiMessage] = useState("");

  useEffect(() => {
    (async () => {
      const r = await api.get(`/companies/${id}`);
      setCompany(r.data);
      const existing = r.data.data || {};
      setAnswers(existing);
      // jump to first unanswered question
      const firstUnanswered = QUESTIONS.findIndex(
        (q) => !(existing[String(q.stage)] || {})[q.key]
      );
      setIdx(firstUnanswered >= 0 ? firstUnanswered : 0);
    })();
  }, [id]);

  const total = QUESTIONS.length;
  const q = QUESTIONS[idx];
  const stageKey = q ? String(q.stage) : "1";
  const currentValue = (answers[stageKey] || {})[q?.key] || "";

  const completedCount = useMemo(() => {
    let c = 0;
    for (const qq of QUESTIONS) {
      if ((answers[String(qq.stage)] || {})[qq.key]) c++;
    }
    return c;
  }, [answers]);

  const setValue = (val) => {
    setAnswers((prev) => ({
      ...prev,
      [stageKey]: { ...(prev[stageKey] || {}), [q.key]: val },
    }));
  };

  const saveStageIfBoundary = async (newIdx) => {
    // when crossing into a new stage, persist the just-finished stage
    if (!q) return;
    const isLast = newIdx >= total;
    const nextStage = isLast ? null : QUESTIONS[newIdx].stage;
    if (nextStage !== q.stage || isLast) {
      setSaving(true);
      try {
        const r = await api.patch(`/companies/${id}/stage`, {
          stage: q.stage,
          data: answers[String(q.stage)] || {},
        });
        setCompany(r.data.company);
        if (r.data.ai_message) setAiMessage(r.data.ai_message);
      } catch (e) {
        toast.error("Error guardando esta etapa: " + (e.response?.data?.detail || ""));
        setSaving(false);
        return false;
      } finally {
        setSaving(false);
      }
    }
    return true;
  };

  const next = async () => {
    if (!q.optional && !currentValue) {
      toast.error(t("Por favor responde antes de continuar."));
      return;
    }
    const newIdx = idx + 1;
    const ok = await saveStageIfBoundary(newIdx);
    if (!ok) return;
    if (newIdx >= total) {
      // finished stages 1-4 → go to teaser generation page
      navigate(`/seller/company/${id}/teaser`);
      return;
    }
    setIdx(newIdx);
    setAiMessage("");
  };

  const back = () => {
    if (idx > 0) {
      setIdx(idx - 1);
      setAiMessage("");
    } else {
      navigate(`/seller/company/${id}`);
    }
  };

  if (!company || !q) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-6 py-20 text-center">
          <span className="font-mono-label text-muted-foreground">{t("Cargando…")}</span>
        </div>
      </Layout>
    );
  }

  const pct = Math.round(((idx) / total) * 100);

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-10 lg:py-16">
        {/* header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={back}
            className="font-mono-label text-muted-foreground inline-flex items-center gap-2 hover:text-foreground"
            data-testid="questionnaire-back-btn"
          >
            <ArrowLeft className="w-3 h-3" />{" "}{t("Atrás")}
          </button>
          <span className="font-mono-label text-gold">
            {t("Pregunta")}{" "}{idx + 1} / {total}{" "}{t("· Etapa")}{" "}{q.stage}
          </span>
        </div>

        {/* progress bar */}
        <div className="h-1 bg-secondary relative mb-4">
          <div className="absolute inset-y-0 left-0 bg-[#1E3A5F] transition-all duration-300" style={{ width: `${pct}%` }} />
        </div>
        <div className="flex justify-between font-mono-label text-muted-foreground mb-12">
          <span>{company.name}</span>
          <span>{completedCount}/{total}{" "}{t("respondidas")}</span>
        </div>

        {/* question card */}
        <div className="fade-up" key={idx}>
          <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl leading-tight">
            {q.title}
            {q.optional && <span className="font-mono-label text-muted-foreground ml-2 align-middle">{t("opcional")}</span>}
          </h2>
          {q.helper && <p className="text-muted-foreground mt-4 max-w-xl">{q.helper}</p>}

          <div className="mt-10">
            {q.type === "textarea" ? (
              <Textarea
                value={currentValue}
                onChange={(e) => setValue(e.target.value)}
                rows={4}
                placeholder={q.placeholder}
                className="rounded-none text-lg"
                autoFocus
                data-testid={`q-input-${q.key}`}
              />
            ) : q.type === "select" ? (
              <Select value={currentValue} onValueChange={setValue}>
                <SelectTrigger className="rounded-none h-14 text-lg" data-testid={`q-input-${q.key}`}>
                  <SelectValue placeholder={t("Selecciona una opción…")} />
                </SelectTrigger>
                <SelectContent>
                  {q.options.map((o) => <SelectItem key={o} value={o}>{t(o)}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <Input
                type={q.type}
                value={currentValue}
                onChange={(e) => setValue(e.target.value)}
                placeholder={q.placeholder}
                className="rounded-none h-14 text-lg"
                autoFocus
                onKeyDown={(e) => { if (e.key === "Enter") next(); }}
                data-testid={`q-input-${q.key}`}
              />
            )}
          </div>

          {aiMessage && (
            <div className="mt-8 hairline p-5 bg-bone flex items-start gap-4 fade-up" data-testid="questionnaire-ai-message">
              <div className="w-9 h-9 hairline flex items-center justify-center bg-[#1E3A5F] flex-shrink-0">
                <Sparkles className="w-4 h-4 text-gold" />
              </div>
              <div>
                <span className="font-mono-label text-gold">{t("Tu asesor AI")}</span>
                <p className="font-serif-display text-lg mt-1 leading-snug">{aiMessage}</p>
              </div>
            </div>
          )}

          <div className="mt-12 flex items-center justify-between">
            <span className="font-mono-label text-muted-foreground">
              {saving ? t("Guardando etapa…") : ""}
            </span>
            <Button
              onClick={next}
              disabled={saving}
              className="rounded-none bg-[#1E3A5F] hover:bg-[#2B4C78] text-bone h-12 px-7"
              data-testid="questionnaire-next-btn"
            >
              {idx + 1 === total ? (
                <>{t("Generar teaser")}{" "}<CheckCircle2 className="w-4 h-4 ml-2" /></>
              ) : (
                <>{t("Siguiente")}{" "}<ArrowRight className="w-4 h-4 ml-2" /></>
              )}
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
